// สร้างไฟล์ใหม่ที่ src/components/AttachmentModal.jsx

import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiX,
  FiInfo,
  FiUploadCloud,
  FiPaperclip,
  FiTrash2,
  FiClock,
  FiCheckCircle,
  FiLoader,
} from "react-icons/fi";
import { format, isBefore, subHours, addDays } from "date-fns";
import { th } from "date-fns/locale";
import api from "../services/api"; // Import a-p-i ของคุณ

const AttachedFileItem = ({ attachment, onRemove, onToggle }) => {
  const now = new Date();
  const expiry = new Date(attachment.expiryDate);
  const isExpired = isBefore(expiry, now);
  const expiresSoon = !isExpired && isBefore(expiry, subHours(now, -1)); // หมดอายุใน 1 ชม.

  let statusColor = "border-slate-300";
  if (expiresSoon) statusColor = "border-yellow-400";
  if (isExpired) statusColor = "border-red-400";

  return (
    <div
      className={`p-2 mb-2 bg-white rounded-lg flex items-center justify-between border-l-4 ${statusColor}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <input
          type="checkbox"
          checked={attachment.useInChat}
          onChange={() => onToggle(attachment.id)}
          disabled={isExpired || attachment.status === "uploading"}
          className="w-5 h-5 rounded text-green-600 focus:ring-green-500 disabled:opacity-50"
        />
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-800 truncate">
            {attachment.fileName}
          </p>
          <div className="text-xs text-slate-500 flex items-center gap-1">
            <FiClock
              size={12}
              className={
                isExpired
                  ? "text-red-500"
                  : expiresSoon
                  ? "text-yellow-500"
                  : ""
              }
            />
            <span>
              {isExpired
                ? "หมดอายุแล้ว"
                : `หมดอายุ ${format(expiry, "d MMM yy, HH:mm", {
                    locale: th,
                  })}`}
            </span>
          </div>
        </div>
      </div>
      <div className="flex items-center flex-shrink-0">
        {attachment.status === "uploading" && (
          <FiLoader className="animate-spin text-slate-400 mr-2" />
        )}
        <button
          onClick={() => onRemove(attachment.id)}
          className="p-1 text-slate-400 hover:text-red-500"
        >
          <FiTrash2 />
        </button>
      </div>
    </div>
  );
};

const AttachmentModal = ({ isOpen, onClose, currentAttachments, onSave }) => {
  const [attachments, setAttachments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setAttachments(currentAttachments || []);
    }
  }, [currentAttachments, isOpen]);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const MAX_TOTAL_FILES = 6;
    if (attachments.length >= MAX_TOTAL_FILES) {
      alert("สามารถแนบไฟล์ได้สูงสุด 6 ไฟล์");
      fileInputRef.current.value = "";
      return;
    }

    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
    if (file.size > MAX_FILE_SIZE) {
      alert("ขนาดไฟล์ต้องไม่เกิน 10 MB");
      fileInputRef.current.value = ""; // Reset input
      return;
    }

    setIsUploading(true);
    const tempId = `temp_${Date.now()}`;
    const expiryDate = addDays(new Date(), 2).toISOString();
    const placeholder = {
      id: tempId,
      fileName: file.name,
      mimetype: file.type,
      status: "uploading",
      expiryDate: expiryDate,
      useInChat: true,
    };
    setAttachments((prev) => [...prev, placeholder]);

    try {
      const response = await api.uploadAttachment(file);
      if (response.success) {
        setAttachments((prev) =>
          prev.map((att) =>
            att.id === tempId
              ? {
                  ...placeholder,
                  status: "active",
                  ...response.data,
                  id: `file_${response.data.fileId}`,
                  expiryDate: expiryDate, // Ensure expiry date is set correctly
                }
              : att
          )
        );
      } else {
        throw new Error("Upload failed");
      }
    } catch (error) {
      console.error(error);
      setAttachments((prev) => prev.filter((att) => att.id !== tempId)); // ลบ placeholder ถ้าล้มเหลว
      alert("ไม่สามารถอัปโหลดไฟล์ได้");
    } finally {
      setIsUploading(false);
    }
    fileInputRef.current.value = ""; // Reset input
  };

  const handleRemove = (id) =>
    setAttachments((prev) => prev.filter((att) => att.id !== id));
  const handleToggle = (id) => {
    setAttachments((prev) =>
      prev.map((att) =>
        att.id === id ? { ...att, useInChat: !att.useInChat } : att
      )
    );
  };

  const handleSave = () => {
    onSave(attachments);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        className="bg-slate-50 rounded-xl shadow-2xl w-full max-w-2xl h-[85vh] flex flex-col"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <div className="p-4 border-b border-slate-200 flex justify-between items-center">
          <h2 className="text-xl font-bold text-green-800 flex items-center gap-2">
            <FiPaperclip /> จัดการไฟล์แนบ
          </h2>
          <button
            onClick={onClose}
            disabled={isUploading}
            className="p-1.5 rounded-full disabled:opacity-50"
          >
            <FiX size={24} className="text-slate-500" />
          </button>
        </div>

        <div className="p-4 flex-1 overflow-y-auto">
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3 mb-4">
            <FiInfo className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-green-800 text-sm">การแนบไฟล์</p>
              <p className="text-xs text-green-700 mt-1">
                เลือกไฟล์ที่ต้องการให้ AI ใช้วิเคราะห์ร่วมกับเนื้อหาการประชุม
                (สูงสุด 6 ไฟล์, ไม่เกิน 10MB ต่อไฟล์, ไฟล์มีอายุ 2 วัน)
              </p>
            </div>
          </div>

          {attachments.map((att) => (
            <AttachedFileItem
              key={att.id}
              attachment={att}
              onRemove={handleRemove}
              onToggle={handleToggle}
            />
          ))}
        </div>

        <div className="p-4 border-t border-slate-200 flex justify-between items-center">
          <label
            htmlFor="file-upload"
            className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all flex items-center gap-1.5 shadow-sm ${
              isUploading || attachments.length >= 6
                ? "bg-slate-300 text-slate-500 cursor-not-allowed"
                : "bg-white text-slate-700 border border-slate-300 hover:bg-slate-100"
            }`}
          >
            <FiUploadCloud /> {isUploading ? "กำลังอัปโหลด..." : "เลือกไฟล์"}
          </label>
          <input
            ref={fileInputRef}
            id="file-upload"
            type="file"
            onChange={handleFileUpload}
            disabled={isUploading || attachments.length >= 6}
            className="hidden"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.gif"
          />

          <button
            onClick={handleSave}
            disabled={isUploading}
            className="px-4 py-2 text-sm font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
          >
            บันทึกและปิด
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default AttachmentModal;
